import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    Output,
    ViewChild,
    forwardRef,
    OnInit,
    OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, Upload, X, File, Image, Eye, ChevronUp, ChevronDown } from 'lucide-angular';

export interface FileUploadItem {
    file: File;
    id: string;
    previewUrl?: string;
    isImage: boolean;
}

@Component({
    selector: 'app-file-upload',
    standalone: true,
    imports: [CommonModule, LucideAngularModule],
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FileUploadComponent),
            multi: true,
        },
    ],
})
export class FileUploadComponent implements OnInit, OnDestroy, ControlValueAccessor {
    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    // Configuration inputs
    @Input() placeholder: string = 'Click to upload or drag and drop';
    @Input() subText: string = 'Supported formats: JPG, PNG, PDF';
    @Input() acceptedTypes: string = '';
    @Input() maxFileSize: number = 5 * 1024 * 1024; // 5MB default
    @Input() maxFiles: number = 1; // Maximum number of files allowed
    @Input() multiple: boolean = false; // Enable multiple file selection
    @Input() disabled: boolean = false;
    @Input() showImageThumbnails: boolean = true; // Show image previews
    @Input() thumbnailSize: string = '80px'; // Thumbnail size

    // Styling inputs
    @Input() containerClass: string = '';
    @Input() showError: boolean = false;
    @Input() showRequirements: boolean = true;
    @Input() requirementsText: string = '';

    // Events
    @Output() filesSelected = new EventEmitter<FileUploadItem[]>();
    @Output() fileRemoved = new EventEmitter<string>(); // Emit file ID
    @Output() validationError = new EventEmitter<string>();
    @Output() previewClicked = new EventEmitter<FileUploadItem>();

    // Icons
    readonly Upload = Upload;
    readonly X = X;
    readonly File = File;
    readonly Image = Image;
    readonly Eye = Eye;
    readonly ChevronUp = ChevronUp;
    readonly ChevronDown = ChevronDown;

    // State
    selectedFiles: FileUploadItem[] = [];
    isDragOver: boolean = false;
    errorMessage: string = '';
    showFilesList: boolean = false; // Controls expanded/collapsed files list
    private objectUrls: Set<string> = new Set(); // Track URLs for cleanup

    // ControlValueAccessor implementation
    private onChange = (value: FileUploadItem[] | FileUploadItem | File | null) => {};
    private onTouched = () => {};

    ngOnInit() {
        if (!this.requirementsText && this.acceptedTypes) {
            const maxFilesText = this.multiple ? ` Max files: ${this.maxFiles}.` : '';
            this.requirementsText = `Accepted formats: ${this.acceptedTypes}. Max size: ${this.formatFileSize(
                this.maxFileSize
            )}.${maxFilesText}`;
        }
    }

    ngOnDestroy() {
        // Clean up object URLs to prevent memory leaks
        this.cleanupObjectUrls();
    }

    private cleanupObjectUrls() {
        this.objectUrls.forEach((url) => {
            URL.revokeObjectURL(url);
        });
        this.objectUrls.clear();
    }

    /**
     * View image in new tab using blob URL
     * @param event Click event
     * @param fileItem File item to view
     */
    viewImageInNewTab(event: Event, fileItem: FileUploadItem): void {
        event.stopPropagation(); // Prevent triggering file input click

        if (!fileItem.isImage || !fileItem.file) {
            return;
        }

        try {
            // Create blob URL for the file
            const blobUrl = URL.createObjectURL(fileItem.file);

            // Open in new tab
            const newWindow = window.open(blobUrl, '_blank');

            if (newWindow) {
                // Clean up the blob URL after the window loads
                newWindow.addEventListener('load', () => {
                    // Delay cleanup to ensure image loads
                    setTimeout(() => {
                        URL.revokeObjectURL(blobUrl);
                    }, 1000);
                });

                // Fallback cleanup in case load event doesn't fire
                setTimeout(() => {
                    URL.revokeObjectURL(blobUrl);
                }, 5000);
            } else {
                // Clean up immediately if popup was blocked
                URL.revokeObjectURL(blobUrl);
                console.warn('Popup blocked. Please allow popups for this site to view images.');
            }
        } catch (error) {
            console.error('Error creating blob URL for image:', error);
        }
    }

    /**
     * Handle browse button click with proper event handling
     * @param event Click event
     */
    onBrowseClick(event: Event): void {
        event.stopPropagation(); // Prevent event bubbling to parent div

        if (!this.disabled) {
            this.fileInput.nativeElement.click();
        }
    }

    onFileSelected(event: Event) {
        const target = event.target as HTMLInputElement;
        const files = target.files;

        if (files && files.length > 0) {
            if (this.multiple) {
                this.handleMultipleFiles(Array.from(files));
            } else {
                this.handleSingleFile(files[0]);
            }
        }
    }

    onDragOver(event: DragEvent) {
        if (this.disabled) return;

        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
    }

    onDragLeave(event: DragEvent) {
        if (this.disabled) return;

        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    onDrop(event: DragEvent) {
        if (this.disabled) return;

        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            if (this.multiple) {
                this.handleMultipleFiles(Array.from(files));
            } else {
                this.handleSingleFile(files[0]);
            }
        }
    }

    private handleSingleFile(file: File) {
        this.errorMessage = '';

        // Check for empty file
        if (file.size === 0) {
            this.errorMessage = `Cannot upload empty file: "${file.name}". Please select a valid file.`;
            this.validationError.emit(this.errorMessage);
            return;
        }

        if (!this.validateFile(file)) {
            return;
        }

        // Clear existing files for single upload
        this.cleanupObjectUrls();
        this.selectedFiles = [];

        const fileItem = this.createFileUploadItem(file);
        this.selectedFiles = [fileItem];

        // For form control: emit File object for single upload, FileUploadItem array for multiple
        this.onChange(this.multiple ? this.selectedFiles : fileItem.file);
        this.onTouched();
        this.filesSelected.emit(this.selectedFiles);
    }

    private handleMultipleFiles(files: File[]) {
        this.errorMessage = '';

        // Check if adding these files would exceed maxFiles
        if (this.selectedFiles.length + files.length > this.maxFiles) {
            this.errorMessage = `Cannot upload more than ${this.maxFiles} files`;
            this.validationError.emit(this.errorMessage);
            return;
        }

        const validFiles: FileUploadItem[] = [];

        for (const file of files) {
            // Check for empty file
            if (file.size === 0) {
                this.errorMessage = `Cannot upload empty file: "${file.name}". Please select a valid file.`;
                this.validationError.emit(this.errorMessage);
                continue;
            }

            if (this.validateFile(file)) {
                validFiles.push(this.createFileUploadItem(file));
            }
        }

        if (validFiles.length > 0) {
            this.selectedFiles = [...this.selectedFiles, ...validFiles];
            this.onChange(this.selectedFiles);
            this.onTouched();
            this.filesSelected.emit(this.selectedFiles);
        }
    }

    private validateFile(file: File): boolean {
        // Validate file type
        if (this.acceptedTypes && !this.isFileTypeAccepted(file)) {
            const fileExtension = file.name.split('.').pop()?.toUpperCase() || 'unknown';
            this.errorMessage = `File type "${fileExtension}" is not supported. Please select a file with one of the accepted formats.`;
            this.validationError.emit(this.errorMessage);
            return false;
        }

        // Validate file size
        if (file.size > this.maxFileSize) {
            this.errorMessage = `File "${file.name}" is too large (${this.formatFileSize(
                file.size
            )}). Maximum allowed size is ${this.formatFileSize(this.maxFileSize)}.`;
            this.validationError.emit(this.errorMessage);
            return false;
        }

        return true;
    }

    private createFileUploadItem(file: File): FileUploadItem {
        const id = this.generateFileId();
        const isImage = this.isImageFile(file);
        let previewUrl: string | undefined;

        if (isImage && this.showImageThumbnails) {
            previewUrl = URL.createObjectURL(file);
            this.objectUrls.add(previewUrl);
        }

        return {
            file,
            id,
            previewUrl,
            isImage,
        };
    }

    private generateFileId(): string {
        return 'file_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    private isImageFile(file: File): boolean {
        return file.type.startsWith('image/');
    }

    removeFile(event: Event, fileId: string) {
        event.stopPropagation();

        const fileIndex = this.selectedFiles.findIndex((f) => f.id === fileId);
        if (fileIndex === -1) return;

        const fileItem = this.selectedFiles[fileIndex];

        // Clean up object URL
        if (fileItem.previewUrl) {
            URL.revokeObjectURL(fileItem.previewUrl);
            this.objectUrls.delete(fileItem.previewUrl);
        }

        // Remove file from array
        this.selectedFiles.splice(fileIndex, 1);
        this.errorMessage = '';

        // Clear the file input if no files remain
        if (this.selectedFiles.length === 0 && this.fileInput) {
            this.fileInput.nativeElement.value = '';
        }

        const value =
            this.selectedFiles.length === 0 ? null : this.multiple ? this.selectedFiles : this.selectedFiles[0].file;

        this.onChange(value);
        this.fileRemoved.emit(fileId);
    }

    openImagePreview(fileItem: FileUploadItem) {
        if (fileItem.previewUrl) {
            window.open(fileItem.previewUrl, '_blank');
            this.previewClicked.emit(fileItem);
        }
    }

    // Getter for template usage
    get hasFiles(): boolean {
        return this.selectedFiles.length > 0;
    }

    get canAddMoreFiles(): boolean {
        return this.multiple && this.selectedFiles.length < this.maxFiles;
    }

    private isFileTypeAccepted(file: File): boolean {
        if (!this.acceptedTypes) return true;

        const acceptedTypes = this.acceptedTypes.split(',').map((type) => type.trim().toLowerCase());
        const fileType = file.type.toLowerCase();
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

        return acceptedTypes.some((type) => {
            if (type.startsWith('.')) {
                return type === fileExtension;
            }
            return fileType.includes(type.replace('*', ''));
        });
    }

    formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';

        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    // ControlValueAccessor methods
    writeValue(value: FileUploadItem[] | FileUploadItem | File | null): void {
        // Clean up existing URLs
        this.cleanupObjectUrls();

        if (value === null) {
            this.selectedFiles = [];
        } else if (Array.isArray(value)) {
            this.selectedFiles = value;
            // Recreate object URLs for images
            this.selectedFiles.forEach((item) => {
                if (item && item.file && item.isImage && this.showImageThumbnails && !item.previewUrl) {
                    item.previewUrl = URL.createObjectURL(item.file);
                    this.objectUrls.add(item.previewUrl);
                }
            });
        } else if (
            value &&
            typeof value === 'object' &&
            'name' in value &&
            'size' in value &&
            'type' in value &&
            !('file' in value)
        ) {
            // Handle File object - create FileUploadItem
            const fileItem = this.createFileUploadItem(value as File);
            this.selectedFiles = [fileItem];
        } else if (value) {
            // Handle FileUploadItem
            const fileUploadItem = value as FileUploadItem;
            this.selectedFiles = [fileUploadItem];
            // Recreate object URL for image
            if (
                fileUploadItem.file &&
                fileUploadItem.isImage &&
                this.showImageThumbnails &&
                !fileUploadItem.previewUrl
            ) {
                fileUploadItem.previewUrl = URL.createObjectURL(fileUploadItem.file);
                this.objectUrls.add(fileUploadItem.previewUrl);
            }
        }
    }

    registerOnChange(fn: (value: FileUploadItem[] | FileUploadItem | File | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    // Toggle files list visibility
    toggleFilesList(): void {
        this.showFilesList = !this.showFilesList;
    }

    // Public method to clear all selected files
    clearFiles(): void {
        this.cleanupObjectUrls();
        this.selectedFiles = [];
        if (this.fileInput) {
            this.fileInput.nativeElement.value = '';
        }
        this.onChange(null);
        this.filesSelected.emit([]);
    }
}
